import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { User, Settings as SettingsIcon, Shield, Camera, Sun, Moon } from 'lucide-react';
import PageContainer from '../components/PageContainer';
import UserAvatar from '../components/UserAvatar';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { validatePassword } from '../utils/validation';
import { COUNTRY_CODES } from '../utils/constants';

export default function Settings({ toggleSidebar }) {
  const { user, updateUser, themePref, changeTheme } = useAuth();

  const [formData, setFormData] = useState({ name: user?.name || '', avatar: user?.avatar || '' });
  const [countryCode, setCountryCode] = useState(() => (user?.phone && user.phone.includes(' ') ? user.phone.split(' ')[0] : "+91"));
  const [phoneNum, setPhoneNum] = useState(() => (user?.phone && user.phone.includes(' ') ? user.phone.split(' ')[1] : ""));
  const [passData, setPassData] = useState({ otp: '', new_password: '' });
  const [passStep, setPassStep] = useState(0);
  const [loading, setLoading] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size > 5000000) return alert("Max 5MB");
    const reader = new FileReader();
    reader.onloadend = () => setFormData(prev => ({ ...prev, avatar: reader.result }));
    if (file) reader.readAsDataURL(file);
  };

  const handleSaveProfile = async () => {
    const rule = COUNTRY_CODES.find(c => c.code === countryCode);
    if (rule && phoneNum.length !== rule.len) return alert(`Phone number for ${rule.name} must be ${rule.len} digits.`);
    setLoading(true);
    try {
      const fullPhone = `${countryCode} ${phoneNum}`;
      const payload = { ...formData, phone: fullPhone };

      const res = await api.put(`/user/profile`, payload);

      updateUser({ ...formData, phone: fullPhone, avatar: res.data.avatar });
      alert("Profile Updated!");
    } catch (e) {
      console.error("Profile update error:", e);
      alert("Update failed.");
    }
    setLoading(false);
  };

  const initiatePassChange = async () => {
    try {
      await api.post(`/user/send-otp`);
      setPassStep(2);
      alert("OTP sent!");
    } catch (e) {
      console.error("OTP send error:", e);
      alert("Failed.");
    }
  };

  const confirmPassChange = async () => {
    try {
      const err = validatePassword(passData.new_password);
      if (err) return alert(err);

      await api.post(`/user/change-password-secure`, {
        email: user.email,
        otp: passData.otp,
        new_password: passData.new_password
      });

      alert("Changed!");
      setPassStep(0);
      setPassData({ otp: '', new_password: '' });
    } catch (e) {
      console.error("Password change error:", e);
      alert("Invalid OTP");
    }
  };

  const currentRule = COUNTRY_CODES.find(c => c.code === countryCode);

  return (
    <PageContainer title="Settings" subtitle="Preferences & Security" toggleSidebar={toggleSidebar}>
      <motion.div className="settings-hero-luxury" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <div className="hero-orb hero-orb-1" aria-hidden="true" />
        <div className="hero-orb hero-orb-2" aria-hidden="true" />

        <div className="hero-profile-text">
          <h2 className="hero-profile-name">{formData.name || 'User'}</h2>
          <p className="hero-profile-email">{user?.email}</p>
        </div>

        <div className="avatar-container-luxury">

          <UserAvatar
            src={formData.avatar}
            name={formData.name}
            size={140}
            fontSize="3.5rem"
            className="avatar-img-luxury"
          />

          <label className="avatar-edit-btn"><Camera size={18} /><input type="file" hidden onChange={handleFileChange} accept="image/*" /></label>
        </div>
      </motion.div>

      <div className="settings-grid" style={{ marginTop: '5rem' }}>
        <div className="settings-card-glass">
          <div className="settings-section-title"><User size={20} color="#3B82F6" /> <span>Profile</span></div>
          <div className="settings-input-group"><label className="settings-input-label">Name</label><input className="settings-input" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} /></div>
          <div className="settings-input-group"><label className="settings-input-label">Phone</label><div className="phone-input-group" style={{ display: 'flex', gap: 8 }}>
            <select className="country-select" style={{ background: 'rgba(15,23,42,0.6)', border: '1px solid #334155', color: 'var(--text-primary)', padding: '10px' }} value={countryCode} onChange={e => setCountryCode(e.target.value)}>{COUNTRY_CODES.map(c => <option key={c.code} value={c.code}>{c.flag} {c.code}</option>)}</select>
            <input className="settings-input" type="tel" placeholder={`${currentRule?.len || 10} digits`} maxLength={currentRule?.len || 15} value={phoneNum} onChange={e => { const val = e.target.value.replace(/\D/g, ''); if (val.length <= (currentRule?.len || 15)) setPhoneNum(val); }} /></div>
          </div>
          <div className="settings-input-group"><label className="settings-input-label">Email</label><input className="settings-input" value={user?.email} disabled style={{ opacity: 0.6 }} /></div>
          <button className="btn-lux-primary" onClick={handleSaveProfile} disabled={loading}>{loading ? 'Saving...' : 'Save Changes'}</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          <div className="settings-card-glass">
            <div className="settings-section-title"><SettingsIcon size={20} color="#F59E0B" /> <span>Appearance</span></div>
            <div className="setting-toggle-row" style={{ display: 'block' }}>
              <div className="setting-info" style={{ marginBottom: 15 }}><strong>Theme Preference</strong></div>
              <div className="theme-switcher-bar">
                <button className={`theme-btn ${themePref === 'light' ? 'active' : ''}`} onClick={() => changeTheme('light')}><Sun size={16} /> Light</button>
                <button className={`theme-btn ${themePref === 'dark' ? 'active' : ''}`} onClick={() => changeTheme('dark')}><Moon size={16} /> Dark</button>
                <button className={`theme-btn ${themePref === 'system' ? 'active' : ''}`} onClick={() => changeTheme('system')}>System</button>
              </div>
            </div>
          </div>
          <div className="settings-card-glass">
            <div className="settings-section-title"><Shield size={20} color="#EF4444" /> <span>Security</span></div>
            {passStep === 0 && <button className="btn-lux-outline" onClick={() => setPassStep(1)} style={{ width: '100%' }}>Change Password</button>}
            {passStep === 1 && (<div className="secure-action-box"><p>Send OTP to {user?.email}?</p><button className="btn-lux-primary" onClick={initiatePassChange} style={{ width: '100%' }}>Send OTP</button><button className="btn-lux-outline" onClick={() => setPassStep(0)} style={{ width: '100%', marginTop: 10 }}>Cancel</button></div>)}
            {passStep === 2 && (<div className="secure-action-box"><input className="settings-input" placeholder="0000" value={passData.otp} onChange={e => setPassData({ ...passData, otp: e.target.value })} style={{ marginBottom: 10, textAlign: 'center' }} /><input className="settings-input" type="password" placeholder="New Password" value={passData.new_password} onChange={e => setPassData({ ...passData, new_password: e.target.value })} style={{ marginBottom: 10 }} /><button className="btn-lux-primary" onClick={confirmPassChange} style={{ width: '100%' }}>Update</button></div>)}
          </div>
        </div>
      </div>
    </PageContainer>
  );
}
